import type { ConversationSummary, PersistedMessage } from "./types";

const SAME_DAY_FORMAT = new Intl.DateTimeFormat("zh-CN", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});
const MONTH_DAY_FORMAT = new Intl.DateTimeFormat("zh-CN", {
  month: "numeric",
  day: "numeric",
});
const FULL_DATE_FORMAT = new Intl.DateTimeFormat("zh-CN", {
  year: "numeric",
  month: "numeric",
  day: "numeric",
});
const WEEKDAY_FORMAT = new Intl.DateTimeFormat("zh-CN", { weekday: "short" });

/** Produces a safe, compact preview from the projected ordered message. */
export function conversationPreview(conversation: ConversationSummary): {
  readonly accent: "draft" | "mention" | null;
  readonly text: string;
} {
  if (conversation.draft?.trim()) {
    return { accent: "draft", text: `[草稿] ${conversation.draft.trim()}` };
  }
  if (conversation.lastMessageSeq === 0) {
    return { accent: null, text: "开始一段新对话" };
  }
  const prefix = conversation.pendingMentionCount > 0 ? "[有人@你] " : "";
  const fallback = conversation.type === "broadcast"
    ? "[广播] 最新消息"
    : conversation.type === "group" ? "[群聊] 最新消息" : "最新消息";
  return {
    accent: conversation.pendingMentionCount > 0 ? "mention" : null,
    text: `${prefix}${conversation.lastMessage === null || conversation.lastMessage === undefined
      ? fallback
      : messagePreview(conversation.lastMessage)}`,
  };
}

/** Maps every protocol content type to a bounded plain-text list preview. */
export function messagePreview(message: PersistedMessage): string {
  switch (message.contentType) {
    case "text":
      return compactText(message.payload, "[文本消息]");
    case "image":
      return "[图片]";
    case "audio":
      return "[语音]";
    case "custom": {
      const payload = jsonRecord(message.payload);
      return typeof payload?.description === "string"
        ? compactText(payload.description, "[自定义消息]")
        : "[自定义消息]";
    }
    case "command": {
      const payload = jsonRecord(message.payload);
      return typeof payload?.name === "string"
        ? `[指令] ${compactText(payload.name, "消息")}`
        : "[指令消息]";
    }
    case "system":
      return compactText(message.payload, "[系统消息]");
    case "media-ref": {
      const payload = jsonRecord(message.payload);
      if (payload?.kind === "video") return "[视频]";
      if (payload?.kind === "file") {
        return typeof payload.fileName === "string"
          ? `[文件] ${compactText(payload.fileName, "文件")}`
          : "[文件]";
      }
      return "[媒体消息]";
    }
    case "ciphertext":
      return "[加密消息]";
    case "edit":
      return "[消息已编辑]";
    case "retract":
      return "[消息已撤回]";
  }
}

/** Formats conversation time with the compact hierarchy used by desktop IM clients. */
export function formatConversationTime(timestamp: number, now = Date.now()): string {
  if (timestamp <= 0) return "";
  const date = new Date(timestamp);
  const today = new Date(now);
  if (sameCalendarDay(date, today)) return SAME_DAY_FORMAT.format(date);

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (sameCalendarDay(date, yesterday)) return "昨天";

  const elapsedDays = Math.floor((startOfDay(today) - startOfDay(date)) / 86_400_000);
  if (elapsedDays > 1 && elapsedDays < 7) return WEEKDAY_FORMAT.format(date);
  return date.getFullYear() === today.getFullYear()
    ? MONTH_DAY_FORMAT.format(date)
    : FULL_DATE_FORMAT.format(date);
}

/** Inserts a divider for the first message, a new day, or a five-minute pause. */
export function shouldShowTimeDivider(
  timestamp: number,
  previousTimestamp: number | undefined,
): boolean {
  if (previousTimestamp === undefined) return true;
  return !sameCalendarDay(new Date(timestamp), new Date(previousTimestamp))
    || timestamp - previousTimestamp >= 5 * 60_000;
}

/** Formats a timeline divider while keeping today's labels compact. */
export function formatMessageTimeDivider(timestamp: number, now = Date.now()): string {
  const date = new Date(timestamp);
  const today = new Date(now);
  const time = SAME_DAY_FORMAT.format(date);
  if (sameCalendarDay(date, today)) return time;

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (sameCalendarDay(date, yesterday)) return `昨天 ${time}`;

  const dateLabel = date.getFullYear() === today.getFullYear()
    ? MONTH_DAY_FORMAT.format(date)
    : FULL_DATE_FORMAT.format(date);
  return `${dateLabel} ${time}`;
}

function sameCalendarDay(left: Date, right: Date): boolean {
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function compactText(value: string, fallback: string): string {
  const compact = value.replace(/\s+/gu, " ").trim();
  if (compact.length === 0) return fallback;
  return compact.length <= 80 ? compact : `${compact.slice(0, 80)}…`;
}

function jsonRecord(value: string): Record<string, unknown> | null {
  try {
    const parsed: unknown = JSON.parse(value);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : null;
  } catch {
    return null;
  }
}
